import { useState } from 'react';
import { AlertCircle, CheckCircle2, Users } from 'lucide-react';
import { useStandupSummary } from '../../api/checkIns';
import { useTeamList } from '../../api/teams';
import PageHeader from '../../components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const SELECT_CLS = 'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function StandupSummaryPage() {
  const [date, setDate]     = useState(today());
  const [teamId, setTeamId] = useState('');

  const { data: teams }   = useTeamList();
  const { data: summary, isLoading } = useStandupSummary(teamId || undefined, date);

  return (
    <div className="max-w-2xl">
      <PageHeader title="Standup summary" />

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Date</p>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={SELECT_CLS}
          />
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Team</p>
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className={cn(SELECT_CLS, 'min-w-[160px]')}>
            <option value="">All teams</option>
            {teams?.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      {summary && (
        <>
          {/* Entries */}
          {summary.entries.length === 0 && summary.missingNames.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-sm text-muted-foreground">
                No check-ins submitted for this date.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {summary.entries.map((entry) => (
                <Card key={entry.engineerId} className="overflow-hidden">
                  <div className="flex items-center gap-2.5 border-b border-border bg-muted/30 px-5 py-3">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <p className="text-sm font-semibold text-foreground">{entry.engineerName}</p>
                  </div>
                  <CardContent className="px-5 py-4 space-y-3">
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Completed</p>
                      <p className="whitespace-pre-wrap text-sm text-foreground">{entry.completed}</p>
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Planned next</p>
                      <p className="whitespace-pre-wrap text-sm text-foreground">{entry.plannedNext}</p>
                    </div>
                    {entry.blockers && (
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900/40 dark:bg-red-950/20">
                        <p className="mb-1 text-xs font-semibold text-red-600">Blockers</p>
                        <p className="whitespace-pre-wrap text-sm text-red-700 dark:text-red-400">{entry.blockers}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Missing */}
          {summary.missingNames.length > 0 && (
            <Card className="mt-5 overflow-hidden border-amber-200 dark:border-amber-900/40">
              <div className="flex items-center gap-2.5 border-b border-amber-200 bg-amber-50 px-5 py-3 dark:border-amber-900/40 dark:bg-amber-950/20">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">
                  Missing ({summary.missingNames.length})
                </p>
              </div>
              <CardContent className="px-5 py-4">
                <div className="flex flex-wrap gap-2">
                  {summary.missingNames.map((name) => (
                    <div key={name} className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1">
                      <Users className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">{name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
